import { z } from "zod";
import { throwPinSchema } from "./api-types.js";
/**
 * Vertrag zwischen Browser und Server fuer den Throw-Bereich.
 *
 * Aufteilung:
 * - Zustandsaendernde Aktionen laufen ueber REST. Dort lassen sich Rate-Limits,
 *   Origin-Pruefung und Statuscodes sauber anwenden.
 * - Der Server benachrichtigt ueber WebSocket. Nur so erfaehrt ein Client von
 *   einer eingehenden Anfrage, ohne pollen zu muessen.
 *
 * Ueber den WebSocket laufen ausschliesslich Steuernachrichten -- niemals
 * Dateiinhalte.
 */
// --- Gemeinsame Bausteine ----------------------------------------------------
/** Neutrale Kurzbezeichnung eines Clients, z. B. "Ruhiger Falke". */
export const displayNameSchema = z.string().min(1).max(40);
/** Zeitpunkte werden durchgaengig als ISO-8601-String uebertragen. */
export const isoTimestampSchema = z.string().datetime();
export const connectionRequestStatusSchema = z.enum([
    "pending",
    "accepted",
    "rejected",
    "expired",
    "cancelled",
]);
// --- REST: Antworten ---------------------------------------------------------
/** Die eigene Sitzung samt aktuell gueltiger PIN. */
export const throwSessionSchema = z.object({
    clientId: z.string(),
    displayName: displayNameSchema,
    pin: throwPinSchema.nullable(),
    pinExpiresAt: isoTimestampSchema.nullable(),
    /** Gesetzt, sobald eine Kopplung besteht. */
    pairing: z
        .object({
        pairingId: z.string(),
        peerName: displayNameSchema,
        expiresAt: isoTimestampSchema,
    })
        .nullable(),
});
/** Antwort auf einen Verbindungsversuch. */
export const connectResultSchema = z.object({
    requestId: z.string(),
    expiresAt: isoTimestampSchema,
});
// --- REST: Anfragen ----------------------------------------------------------
export const connectRequestSchema = z.object({
    pin: throwPinSchema,
});
// --- WebSocket: Server an Client --------------------------------------------
/**
 * Eine eingehende Verbindungsanfrage.
 *
 * Enthaelt bewusst keine IP-Adresse und keine Kennung des Anfragenden, die sich
 * ueber mehrere Sitzungen verfolgen liesse. Der Empfaenger soll entscheiden, ob
 * er eine Verbindung erwartet -- nicht, wer der andere ist.
 */
export const incomingRequestMessageSchema = z.object({
    type: z.literal("request.incoming"),
    requestId: z.string(),
    fromName: displayNameSchema,
    createdAt: isoTimestampSchema,
    expiresAt: isoTimestampSchema,
});
/** Ergebnis einer selbst gestellten Anfrage. */
export const requestResolvedMessageSchema = z.object({
    type: z.literal("request.resolved"),
    requestId: z.string(),
    status: connectionRequestStatusSchema,
});
export const pairingCreatedMessageSchema = z.object({
    type: z.literal("pairing.created"),
    pairingId: z.string(),
    peerName: displayNameSchema,
    expiresAt: isoTimestampSchema,
});
export const pairingClosedMessageSchema = z.object({
    type: z.literal("pairing.closed"),
    pairingId: z.string(),
    reason: z.enum(["peer_left", "expired", "disconnected"]),
});
/** Neue PIN nach Ablauf oder Erneuerung. */
export const pinChangedMessageSchema = z.object({
    type: z.literal("pin.changed"),
    pin: throwPinSchema.nullable(),
    expiresAt: isoTimestampSchema.nullable(),
});
export const serverErrorMessageSchema = z.object({
    type: z.literal("error"),
    code: z.string(),
    message: z.string(),
});
export const pongMessageSchema = z.object({ type: z.literal("pong") });
// Die vollstaendige Union aller Servernachrichten steht in
// transfer-protocol.ts -- erst dort sind auch die Transfernachrichten bekannt.
// --- WebSocket: Client an Server --------------------------------------------
/**
 * Der Client sendet nur ein Lebenszeichen.
 *
 * Alles Zustandsaendernde laeuft ueber REST. Damit bleibt die Angriffsflaeche
 * des WebSockets minimal: eine einzige Nachrichtenart ohne Nutzdaten.
 */
export const clientMessageSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("ping") }),
]);
/** Groessenlimit fuer eine einzelne WebSocket-Nachricht. */
export const WS_MAX_MESSAGE_BYTES = 4 * 1024;
//# sourceMappingURL=throw-protocol.js.map